import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { FileSignature, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import EInvoicePreviewDialog from "@/components/taxes/EInvoicePreviewDialog";

export default function EInvoicing() {
    const [items, setItems] = useState([]);
    const [eligible, setEligible] = useState([]);
    const [preview, setPreview] = useState({ open: false, id: null });
    const navigate = useNavigate();

    const load = async () => {
        const [e, i] = await Promise.all([
            api.get("/e-invoices"),
            api.get("/invoices", { params: { type: "invoice" } }),
        ]);
        setItems(e.data);
        setEligible(i.data.filter((x) => !x.e_invoice?.irn && x.status !== "void" && x.status !== "draft" && x.currency === "INR"));
    };
    useEffect(() => { load(); }, []);

    const generate = async (id) => {
        try {
            await api.post(`/e-invoices/${id}/generate`);
            toast.success("IRN generated");
            await load();
        } catch (e) { toast.error("Failed"); }
    };

    const cancel = async (id) => {
        await api.post(`/e-invoices/${id}/cancel`);
        toast.success("IRN cancelled");
        await load();
    };

    return (
        <>
            <PageHeader kicker="TAXES" title="E-Invoicing (IRN/IRP)" subtitle="NIC IRP integration architecture — sandbox now, live API plug-in later." />
            <PageBody>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-card border border-border rounded-md overflow-hidden">
                        <div className="px-5 py-4 border-b border-border">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">ELIGIBLE INVOICES</div>
                            <div className="text-[17px] font-semibold mt-1">Awaiting IRN</div>
                        </div>
                        <table className="fin-table">
                            <thead><tr><th>Invoice</th><th>Customer</th><th className="num">Amount</th><th></th></tr></thead>
                            <tbody>
                                {eligible.slice(0, 20).map((i) => (
                                    <tr key={i.id}>
                                        <td className="font-mono text-[12.5px]">{i.number}</td>
                                        <td className="truncate max-w-[200px]">{i.customer_name}</td>
                                        <td className="num">{fmtMoney(i.total)}</td>
                                        <td className="text-right space-x-1">
                                            <Button size="sm" variant="ghost" onClick={() => setPreview({ open: true, id: i.id })} data-testid={`einv-preview-${i.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Preview</Button>
                                            <Button size="sm" variant="outline" onClick={() => generate(i.id)} data-testid={`einv-gen-${i.id}`}><FileSignature className="w-3.5 h-3.5 mr-1" />Generate</Button>
                                        </td>
                                    </tr>
                                ))}
                                {eligible.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-[12px] text-muted-foreground">No eligible invoices.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <div className="bg-card border border-border rounded-md overflow-hidden">
                        <div className="px-5 py-4 border-b border-border">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">RECENT IRNs</div>
                            <div className="text-[17px] font-semibold mt-1">E-invoice log</div>
                        </div>
                        <table className="fin-table">
                            <thead><tr><th>Invoice</th><th>IRN (truncated)</th><th>Status</th><th></th></tr></thead>
                            <tbody>
                                {items.slice(0, 30).map((r) => (
                                    <tr key={r.id} className="cursor-pointer" onClick={() => navigate(`/sales/invoices/${r.invoice_id}`)}>
                                        <td className="font-mono text-[12.5px]">{r.invoice_number}</td>
                                        <td className="font-mono text-[11px]">{(r.irn || "").slice(0, 18)}…</td>
                                        <td><StatusBadge status={r.status} /></td>
                                        <td>{r.status === "generated" && <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); cancel(r.id); }}><X className="w-3.5 h-3.5" /></Button>}</td>
                                    </tr>
                                ))}
                                {items.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-[12px] text-muted-foreground">No IRNs generated yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </PageBody>
            <EInvoicePreviewDialog
                open={preview.open}
                onClose={() => setPreview({ open: false, id: null })}
                invoiceId={preview.id}
                onGenerated={load}
            />
        </>
    );
}
